/** Foo Industries — complete, modelled, and waiting on a human. */
import { dollars } from "@/lib/format";
import type { Borrower, CaseRecord } from "@/lib/types";

const FS = { documentId: "doc-foo-fs", documentName: "2025 Audited Financial Statements" };
const COV = { documentId: "doc-foo-cov", documentName: "Covenant Compliance Certificate" };

export const fooBorrower: Borrower = {
  id: "foo",
  name: "Foo Industries",
  industry: "Speciality chemicals",
  relationshipSince: 2023,
  riskGrade: "B-",
  totalExposure: dollars(2_050_000),
  headquarters: "Bay City, Michigan",
  employees: 162,
};

export const fooCase: CaseRecord = {
  case: {
    id: "foo-2026-eq",
    reference: "CR-2026-0402",
    borrowerId: "foo",
    borrowerName: "Foo Industries",
    purpose: "Equipment Finance",
    requestedAmount: dollars(1_200_000),
    stage: "approval",
    riskGrade: "B-",
    openedAt: "2026-07-29T08:00:00Z",
    owner: "Tom Giladi",
    summary: [
      "Evidence is complete, all four models have run and no exceptions remain open. The case is waiting on a credit decision.",
      "The proposal is a $1.2M equipment facility against $1.48M of appraised plant. Coverage is 1.23x on a forced-sale basis.",
      "Leverage is the constraint: 3.41x today against a 3.50x policy cap, leaving 3% headroom before the new facility draws.",
    ],
    risks: [
      {
        id: "foo-risk-leverage",
        label: "Leverage 3.41x against a 3.50x policy cap",
        trace: {
          claim: "Leverage 3.41x",
          derivation: ["Analysis · Financials", "Total debt $3,120,000 ÷ adjusted EBITDA $915,000"],
          evidence: { ...FS, page: 21, section: "Note 11 — Borrowings" },
        },
      },
      { id: "foo-risk-cyclical", label: "Speciality chemicals demand is cyclical and softening" },
      { id: "foo-risk-single-site", label: "Single production site — no operational redundancy" },
    ],
    strengths: [
      {
        id: "foo-strength-coverage",
        label: "Equipment collateral covers the facility 1.23x",
        trace: {
          claim: "Collateral coverage 1.23x",
          derivation: ["Analysis · Financials", "Forced-sale appraisal $1,476,000 ÷ facility $1,200,000"],
          evidence: { documentId: "doc-foo-appraisal", documentName: "Equipment Appraisal — Hilco", page: 2 },
        },
      },
      { id: "foo-strength-audited", label: "Audited statements, clean opinion, three consecutive years" },
      { id: "foo-strength-covenant", label: "No covenant breach across eight reporting quarters" },
    ],
    headlineMetrics: [
      { id: "foo-m-revenue", label: "Revenue", value: "$11.40M" },
      { id: "foo-m-ebitda", label: "Adjusted EBITDA", value: "$915K" },
      { id: "foo-m-dscr", label: "DSCR", value: "1.19x" },
      { id: "foo-m-leverage", label: "Debt / EBITDA", value: "3.41x" },
    ],
    openQuestions: [],
  },

  evidence: [
    {
      id: "doc-foo-fs",
      caseId: "foo-2026-eq",
      kind: "document",
      name: "2025 Audited Financial Statements",
      provider: "Borrower upload · Audited by Grant Mercer LLP",
      receivedAt: "2026-07-30T10:00:00Z",
      pages: 44,
      status: "received",
      extracted: [
        {
          id: "ev-foo-revenue",
          label: "Revenue",
          value: "$11,400,000",
          amount: dollars(11_400_000),
          confidence: 0.99,
          status: "verified",
          page: 9,
          section: "Consolidated statement of operations",
        },
        {
          id: "ev-foo-ebitda",
          label: "Adjusted EBITDA",
          value: "$915,000",
          amount: dollars(915_000),
          confidence: 0.97,
          status: "verified",
          page: 11,
          section: "Non-GAAP reconciliation",
        },
        {
          id: "ev-foo-debt",
          label: "Total borrowings",
          value: "$3,120,000",
          amount: dollars(3_120_000),
          confidence: 0.98,
          status: "verified",
          page: 21,
          section: "Note 11 — Borrowings",
        },
      ],
    },
    {
      id: "doc-foo-appraisal",
      caseId: "foo-2026-eq",
      kind: "external",
      name: "Equipment Appraisal — Hilco",
      provider: "Hilco Valuation Services · 12 Aug 2026",
      receivedAt: "2026-08-12T14:00:00Z",
      pages: 28,
      status: "received",
      extracted: [
        {
          id: "ev-foo-fmv",
          label: "Fair market value",
          value: "$1,940,000",
          amount: dollars(1_940_000),
          confidence: 0.95,
          status: "verified",
          page: 2,
          section: "Valuation summary",
        },
        {
          id: "ev-foo-flv",
          label: "Forced liquidation value",
          value: "$1,476,000",
          amount: dollars(1_476_000),
          confidence: 0.95,
          status: "verified",
          page: 2,
          section: "Valuation summary",
        },
      ],
    },
    {
      id: "doc-foo-cov",
      caseId: "foo-2026-eq",
      kind: "accounting",
      name: "Covenant Compliance Certificate",
      provider: "Borrower CFO · quarterly",
      receivedAt: "2026-08-05T09:30:00Z",
      pages: 3,
      status: "received",
      extracted: [
        {
          id: "ev-foo-leverage",
          label: "Reported leverage ratio",
          value: "3.41x",
          confidence: 0.99,
          status: "verified",
          page: 1,
          section: "Covenant calculations",
          note: "Policy cap is 3.50x.",
        },
        {
          id: "ev-foo-dscr",
          label: "Reported DSCR",
          value: "1.19x",
          confidence: 0.99,
          status: "verified",
          page: 1,
          section: "Covenant calculations",
        },
      ],
    },
  ],

  analysis: {
    caseId: "foo-2026-eq",
    financials: {
      periods: ["2023", "2024", "2025", "LTM"],
      rows: [
        {
          id: "foo-fin-revenue",
          label: "Revenue",
          values: { "2023": "$9.80M", "2024": "$10.60M", "2025": "$11.40M", LTM: "$11.20M" },
          emphasis: true,
        },
        {
          id: "foo-fin-ebitda",
          label: "Adjusted EBITDA",
          values: { "2023": "$862K", "2024": "$904K", "2025": "$915K", LTM: "$890K" },
          emphasis: true,
          trace: {
            claim: "Adjusted EBITDA $915K in 2025",
            derivation: ["Non-GAAP reconciliation"],
            evidence: { ...FS, page: 11, section: "Non-GAAP reconciliation", extractedValueId: "ev-foo-ebitda" },
          },
        },
        { id: "foo-fin-margin", label: "EBITDA margin", values: { "2023": "8.8%", "2024": "8.5%", "2025": "8.0%", LTM: "7.9%" } },
        {
          id: "foo-fin-debt",
          label: "Total borrowings",
          values: { "2023": "$2.41M", "2024": "$2.78M", "2025": "$3.12M", LTM: "$3.12M" },
          trace: {
            claim: "Total borrowings $3.12M",
            derivation: ["Note 11 — Borrowings"],
            evidence: { ...FS, page: 21, section: "Note 11 — Borrowings", extractedValueId: "ev-foo-debt" },
          },
        },
        {
          id: "foo-fin-leverage",
          label: "Debt / EBITDA",
          values: { "2023": "2.80x", "2024": "3.08x", "2025": "3.41x", LTM: "3.51x" },
          trace: {
            claim: "Debt / EBITDA 3.41x",
            derivation: ["$3,120,000 ÷ $915,000 = 3.41x"],
            evidence: { ...COV, page: 1, section: "Covenant calculations", extractedValueId: "ev-foo-leverage" },
          },
        },
      ],
    },
    bridge: {
      reportedLabel: "Reported EBITDA",
      reported: dollars(838_000),
      adjustments: [
        {
          id: "foo-adj-restructuring",
          label: "Restructuring charge",
          direction: "add",
          amount: dollars(112_000),
          reason: "Line-2 decommissioning completed in Q3 2025. Non-recurring.",
          user: "Tom Giladi",
          at: "2026-08-04T10:22:00Z",
        },
        {
          id: "foo-adj-grant",
          label: "State energy grant",
          direction: "subtract",
          amount: dollars(35_000),
          reason: "One-off Michigan efficiency grant. Not part of operating earnings.",
          user: "Tom Giladi",
          at: "2026-08-04T10:24:00Z",
        },
      ],
    },
    models: [
      {
        id: "foo-model-credit",
        name: "Credit Model",
        version: "v4",
        result: "B-",
        tone: "neutral",
        ranAt: "2026-08-20T16:40:00Z",
        drivers: ["Leverage 3.41x", "DSCR 1.19x", "Audited statements", "Collateral coverage 1.23x"],
      },
      {
        id: "foo-model-cashflow",
        name: "Cash Flow Model",
        version: "v2",
        result: "B",
        tone: "neutral",
        ranAt: "2026-08-20T16:40:00Z",
        drivers: ["Deposit variance 9.1%", "Seasonal Q1 trough"],
      },
      {
        id: "foo-model-fraud",
        name: "Fraud Model",
        version: "v1",
        result: "Low",
        tone: "positive",
        ranAt: "2026-08-20T16:40:00Z",
        drivers: ["Audited source", "No entity-graph anomalies"],
      },
      {
        id: "foo-model-industry",
        name: "Industry Model",
        version: "v3",
        result: "C+",
        tone: "negative",
        ranAt: "2026-08-20T16:40:00Z",
        drivers: ["Speciality chemicals demand softening", "Feedstock volatility"],
      },
    ],
    cashFlow: [
      { month: "Mar 2026", inflow: dollars(921_400), outflow: dollars(878_200), closing: dollars(412_800) },
      { month: "Apr 2026", inflow: dollars(948_100), outflow: dollars(901_600), closing: dollars(459_300) },
      { month: "May 2026", inflow: dollars(902_700), outflow: dollars(913_400), closing: dollars(448_600) },
      { month: "Jun 2026", inflow: dollars(967_300), outflow: dollars(889_100), closing: dollars(526_800) },
    ],
    forecast: [
      { id: "foo-fc-revenue", label: "Revenue", base: "$11.90M", downside: "$10.20M", upside: "$12.80M" },
      { id: "foo-fc-ebitda", label: "Adjusted EBITDA", base: "$948K", downside: "$712K", upside: "$1.10M" },
      { id: "foo-fc-dscr", label: "DSCR", base: "1.14x", downside: "0.86x", upside: "1.33x" },
      { id: "foo-fc-leverage", label: "Debt / EBITDA", base: "3.72x", downside: "4.95x", upside: "3.21x" },
    ],
    covenantHeadroom: [
      { id: "foo-cov-leverage", label: "Leverage cap 3.50x", value: "3.41x · 3% headroom" },
      { id: "foo-cov-dscr", label: "DSCR floor 1.10x", value: "1.19x · 8% headroom" },
      { id: "foo-cov-post", label: "Leverage after new facility", value: "3.72x · breaches cap" },
    ],
  },

  decision: {
    caseId: "foo-2026-eq",
    kind: "approve_with_conditions",
    amount: dollars(1_000_000),
    termMonths: 48,
    pricing: "Prime + 5.4%",
    riskGrade: "B-",
    confidence: "medium",
    positives: [
      { id: "foo-pos-collateral", label: "Forced-sale collateral coverage 1.23x" },
      { id: "foo-pos-audited", label: "Three years of audited statements, clean opinion" },
      { id: "foo-pos-covenant", label: "Eight quarters without a covenant breach" },
    ],
    negatives: [
      { id: "foo-neg-leverage", label: "Leverage reaches 3.72x after drawdown, above the 3.50x cap" },
      { id: "foo-neg-dscr", label: "Downside DSCR 0.86x" },
      { id: "foo-neg-cyclical", label: "Sector demand softening" },
    ],
    conditions: [
      {
        id: "foo-cond-stepdown",
        label: "Leverage step-down to 3.25x by Q4 2027",
        selected: true,
        rationale: "Brings the facility back inside policy within eighteen months.",
      },
      {
        id: "foo-cond-equipment",
        label: "First lien on financed equipment",
        selected: true,
        rationale: "The collateral is the basis of the recommendation.",
      },
      {
        id: "foo-cond-quarterly",
        label: "Quarterly covenant certificates",
        selected: true,
        rationale: "Already the borrower's practice. Formalise it.",
      },
      {
        id: "foo-cond-capex",
        label: "Capex limit $400K per annum",
        selected: false,
        rationale: "Considered and not proposed — it would constrain the expansion the facility funds.",
      },
    ],
    outcome: null,
  },

  activity: [
    {
      id: "act-foo-1",
      at: "2026-08-20T16:40:00Z",
      actor: "Model runner",
      kind: "model",
      title: "All four models completed",
      detail: "Credit B- · Cash Flow B · Fraud Low · Industry C+",
    },
    {
      id: "act-foo-2",
      at: "2026-08-20T16:45:00Z",
      actor: "Tom Giladi",
      kind: "stage",
      title: "Moved to Approval",
      from: "Underwriting",
      to: "Approval",
    },
    {
      id: "act-foo-3",
      at: "2026-08-12T14:00:00Z",
      actor: "Hilco Valuation Services",
      kind: "evidence",
      title: "Equipment appraisal received",
      detail: "FMV $1.94M · FLV $1.48M",
    },
    {
      id: "act-foo-4",
      at: "2026-08-04T10:24:00Z",
      actor: "Tom Giladi",
      kind: "adjustment",
      title: "EBITDA adjusted",
      from: "$838K",
      to: "$915K",
      reason: "Restructuring add-back $112K, state energy grant removed $35K",
    },
    {
      id: "act-foo-5",
      at: "2026-07-29T08:00:00Z",
      actor: "Tom Giladi",
      kind: "stage",
      title: "Case opened",
      detail: "Equipment Finance · $1,200,000 requested",
    },
  ],
};
